import { Link } from 'react-router';
import { TileIcon } from './TileIcon';

/** 82px is the kit's floor for a widget card, which is what keeps a one-line card tappable. */
const CARD =
  'flex h-full min-h-[82px] flex-col justify-between gap-3 rounded-card border border-border bg-surface p-4 text-left';

interface StatCardProps {
  iconKey: string | null;
  label: string;
  subtitle?: string | null;
  /** Omitted when there is nothing to count yet, or nothing this visitor may be shown. */
  value?: number | string;
  to?: string;
  /** Why a card without a destination has none. Replaces the subtitle when it applies. */
  note?: string;
}

export function StatCard({ iconKey, label, subtitle, value, to, note }: StatCardProps) {
  // The brand kit has no padlock, so the state has to be readable rather than iconographic:
  // a card that does nothing and explains nothing reads as broken.
  const caption = to ? subtitle : (note ?? subtitle);

  const body = (
    <>
      {/* One ink for every icon in the grid: a row of tiles reads as one row, and a card that
          is waiting for accounts says so in words rather than by fading its glyph. */}
      <TileIcon iconKey={iconKey} className="text-ink-2" />

      <div className="min-w-0">
        {value !== undefined && (
          <p className="font-display text-stat font-semibold tabular-nums text-ink">{value}</p>
        )}
        <p
          className={`text-row font-semibold break-words text-ink ${value === undefined ? '' : 'mt-2'}`}
        >
          {label}
        </p>
        {caption && (
          <p className={`text-subtitle mt-0.5 break-words ${to ? 'text-ink-3' : 'text-ink-4'}`}>
            {caption}
          </p>
        )}
      </div>
    </>
  );

  if (!to) {
    // The feature exists; the door opens at sign-in. Keeping the card in place is what makes
    // the app's shape legible before there are accounts.
    return (
      <div className={CARD} aria-disabled="true">
        {body}
      </div>
    );
  }

  return (
    <Link to={to} className={CARD}>
      {body}
    </Link>
  );
}
